import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogHeader,
    DialogRoot,
    Heading,
    LoadingIndicator,
    useDialogClose,
} from '@gitbook/spine-react';
import { usePersistentValue } from '@gitbook/util-react-hooks';

import { getSiteSpacesFromStructure } from '../../../state';
import { SpaceCreationDialog } from '../../Space';
import { resolvePageTarget } from '../../Space/creation/pageTree';
import { CreateSiteVariantForm, type CreateSiteVariantFormProps } from './CreateSiteVariantForm';
import { useSpaceRevisions } from './useSpaceRevisions';

export interface CreateSiteVariantDialogProps {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    siteSection: api.SiteSection | null;
    onCreateVariant?: (siteSpace: api.SiteSpace, siteSection: api.SiteSection | null) => void;
}

export function CreateSiteVariantDialog(props: CreateSiteVariantDialogProps) {
    const [variantSelection, setVariantSelection] = React.useState<{
        space: api.Space | null;
        template: api.SpaceTemplate | null;
        pageId: string | null;
    } | null>(null);
    const persistentVariantSelection = usePersistentValue(variantSelection);
    const close = useDialogClose();

    const sectionDefaultSpace = props.siteSection?.siteSpaces.find(
        (siteSpace) => siteSpace.default
    )?.space;
    const collectionId =
        sectionDefaultSpace?.parent ??
        getSiteSpacesFromStructure(props.siteStructure)[0]?.space.parent ??
        undefined;

    return (
        <>
            <SpaceCreationDialog
                organizationId={props.organization.id}
                collectionId={collectionId}
                siteContentLayout
                target={{
                    type: 'siteVariant',
                    props: {
                        site: props.site,
                        siteSection: props.siteSection,
                        onCreateSpace: (space, options) =>
                            setVariantSelection({
                                space,
                                template: null,
                                pageId: options?.pageId ?? null,
                            }),
                        onSelectBlankSpace: () =>
                            setVariantSelection({ space: null, template: null, pageId: null }),
                        onSelectTemplate: (template) =>
                            setVariantSelection({ space: null, template, pageId: null }),
                    },
                }}
            />
            {persistentVariantSelection ? (
                <DialogRoot
                    isOpen={Boolean(variantSelection)}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            setVariantSelection(null);
                            close();
                        }
                    }}
                >
                    <CreateSiteVariantDetailsDialog
                        organization={props.organization}
                        site={props.site}
                        siteSection={props.siteSection}
                        space={persistentVariantSelection.space}
                        template={persistentVariantSelection.template}
                        pageId={persistentVariantSelection.pageId}
                        onCreateVariant={props.onCreateVariant}
                    />
                </DialogRoot>
            ) : null}
        </>
    );
}

function CreateSiteVariantDetailsDialog(props: CreateSiteVariantFormProps) {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Name the variant</Heading>
                <Description>One last step before adding the variant.</Description>
            </DialogHeader>
            {props.pageId && props.space ? (
                <React.Suspense
                    fallback={
                        <div className="flex justify-center p-10">
                            <LoadingIndicator size="large" />
                        </div>
                    }
                >
                    <VariantForm {...props} space={props.space} />
                </React.Suspense>
            ) : (
                <CreateSiteVariantForm {...props} />
            )}
        </Dialog>
    );
}

function VariantForm(props: CreateSiteVariantFormProps & { space: api.Space }) {
    const { space, pageId } = props;
    const revisions = useSpaceRevisions([space]);
    const defaultTitle = resolvePageTarget(revisions.get(space.id), pageId)?.title;
    return <CreateSiteVariantForm {...props} defaultTitle={defaultTitle} />;
}
